import { MATCHDAY_COUNT, SWAP_MATCHDAY } from '../../constants';
import type { Country } from '../../models/country';
import type { gameResult } from '../../models/gameUpdate';
import type { ProgressionAward } from '../../models/scoring';
import { buildCPM, buildMPM, computeScoreHistory, scoreAtMatchday } from './matchdayScoring';

export interface ComputeListScoreOptions {
  listIndexSwapPair?: [number, number] | null;
  /** Baseline MatchDay; points through this day are forfeited. Defaults to 0. */
  mdReset?: number;
  /** Swapped order applies for MatchDays strictly greater than this value. */
  swapMatchday?: number;
  matchdayCount?: number;
  /** Out-of-groups (+3) and other progression merged into the MPM at settlement. */
  progressionAwards?: ProgressionAward[];
  /**
   * Score as held at this settled MatchDay (same as `competitions/{tournament}.currentMatchday`).
   * Defaults to the final MatchDay (full tournament preview).
   */
  atMatchday?: number;
}

/**
 * Client-side mirror of the backend MatchDay scoring engine
 * (`functions/src/matchday/`). Builds the CPM from the supplied results and evaluates
 * the user's cumulative score U at the final MatchDay.
 *
 * Used for previews (e.g. the AI League) where the authoritative `currentScore` is not
 * available; pass `atMatchday` and `progressionAwards` to mirror end-of-matchday settlement.
 */
export function computeListScore(
  countryList: Country[],
  gameResults: gameResult[],
  options?: ComputeListScoreOptions,
): number {
  if (countryList.length !== 10) {
    return 0;
  }

  const matchdayCount = options?.matchdayCount ?? MATCHDAY_COUNT;
  const swapMatchday = options?.swapMatchday ?? SWAP_MATCHDAY;
  const mpm = buildMPM(gameResults, matchdayCount, options?.progressionAwards ?? []);
  const cpm = buildCPM(mpm, matchdayCount);

  const scoreHistory = computeScoreHistory({
    countryList,
    swapPair: options?.listIndexSwapPair ?? null,
    mdReset: options?.mdReset ?? 0,
    swapMatchday,
    cpm,
    matchdayCount,
  });

  const atMatchday = options?.atMatchday ?? matchdayCount;
  return scoreAtMatchday(scoreHistory, atMatchday);
}
